import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Alert, Badge } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCaseUsers, updateCaseUsers } from '../../api/cases';

interface CaseUsersProps {
  case_id: string;
}

interface User {
  email: string;
  name?: string;
  organization?: string;
  role?: string;
  groups?: string[];
}

const CaseUsers: React.FC<CaseUsersProps> = ({ case_id }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const auth = useAuth();
  const [processingUsers, setProcessingUsers] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadUsers();
  }, [case_id]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetchCaseUsers(case_id, auth.user?.id_token);
      setUsers(response.data.users);
      setAssignedUsers(response.data.assigned_users || []);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = async (email: string) => {
    setProcessingUsers(prev => new Set(prev).add(email));
    try {
      const newAssignedUsers = assignedUsers.includes(email)
        ? assignedUsers.filter(u => u !== email)
        : [...assignedUsers, email];

      await updateCaseUsers(case_id, newAssignedUsers, auth.user?.id_token);
      setAssignedUsers(newAssignedUsers);
      setSuccess('Users updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update users');
      console.error(err);
    } finally {
      setProcessingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(email);
        return newSet;
      });
    }
  };

  const handleAccessToggle = async (user: User) => {
    setProcessingUsers(prev => new Set(prev).add(user.email));
    try {
      const newAssignedUsers = assignedUsers.includes(user.email)
        ? assignedUsers.filter(u => u !== user.email)
        : [...assignedUsers, user.email];

      await updateCaseUsers(case_id, newAssignedUsers, auth.user?.id_token);
      setAssignedUsers(newAssignedUsers);
      setSuccess('Users updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update users');
      console.error(err);
    } finally {
      setProcessingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(user.email);
        return newSet;
      });
    }
  };

  if (loading) return <div>Loading users...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  // Check if any attorneys are assigned
  const assignedAttorneys = users.filter(u => u.role === 'Attorney' && assignedUsers.includes(u.email));

  return (
    <div>
      <h3>Case Users</h3>
      <p className="text-muted">
        Toggle access to allow users to view and work on this case.
      </p>
      
      {success && <Alert variant="success">{success}</Alert>}
      
      {assignedAttorneys.length > 0 && (
        <Alert variant="info" className="d-flex align-items-start">
          <i className="bi bi-info-circle me-2 mt-1"></i>
          <div>
            <strong>Attorney Access:</strong> Attorneys have limited access - they can upload files and view requirements, 
            but cannot access analyses, extracted data, or case settings.
          </div>
        </Alert>
      )}
      
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th>Access</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.email} style={{ opacity: processingUsers.has(user.email) ? 0.5 : 1 }}>
              <td>
                {user.name || user.email}
                {user.name && <div className="text-muted small">{user.email}</div>}
              </td>
              <td>
                {user.role === 'Attorney' && (
                  <Badge bg="info">
                    <i className="bi bi-person-badge me-1"></i>
                    Attorney
                  </Badge>
                )}
                {user.role === 'SuperAdmin' && (
                  <Badge bg="danger">Super Admin</Badge>
                )}
                {user.role === 'OrgAdmin' && (
                  <Badge bg="warning" text="dark">Org Admin</Badge>
                )}
                {user.role === 'User' && (
                  <Badge bg="secondary">User</Badge>
                )}
                {!user.role && (
                  <Badge bg="secondary">User</Badge>
                )}
              </td>
              <td>
                <Form.Check
                  type="switch"
                  checked={assignedUsers.includes(user.email)}
                  onChange={() => handleAccessToggle(user)}
                  disabled={processingUsers.has(user.email)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default CaseUsers; 